import { useContext } from 'react';
import { format, isThisYear, isToday, isValid, isYesterday } from 'date-fns';
import { Flex, Spacer } from '@zillow/constellation';
import { Anchor, Divider, Text, TextButton } from '@zillow/constellation-10';
import { LoadingTag, LoadingText } from '@zg-rentals/loading-components';

import { RMWContext } from '../../../providers/RMWContext';
import { LoadingMapContext } from '../../../providers/LoadingMapContext/context';

import { useSelector } from '../../../selectors/useSelector';
import { getTagTypeByKey, STATUS_KEY } from '../../../utils/statusTagUtils';

import { StyledLineClamp, StyledRespondNowButtonContainer } from '../../../StyledComponents/StyledComponents';
import { singleTextLineStyles } from '../../../styles';

export type Lead = {
  conversationId: string;
  latestContact: {
    isPhoneLead: boolean;
    isRead: boolean;
    messageDateMs: string;
    messageText: string;
    notRepliedTo: boolean;
  };
  renterInfo: {
    renterPhoneNumber: string;
    renterName: string;
  };
  status: {
    isConversationBlockedByZillow: boolean;
  };
  statusLabel?: {
    linkId: string;
    text: string;
  };
};

type Props = {
  lead: Lead;
  includeDivider: boolean;
};

const getMessageDate = (messageDateMs = ''): string => {
  if (!isValid(new Date(+messageDateMs))) {
    return '';
  }

  if (isYesterday(new Date(+messageDateMs))) {
    return 'Yesterday';
  }

  if (isToday(new Date(+messageDateMs))) {
    return format(new Date(+messageDateMs), 'h:mm a');
  }

  const formatToken = isThisYear(new Date(+messageDateMs)) ? 'MMM do' : 'MMM do, yyyy';
  return format(new Date(+messageDateMs), formatToken);
};

const LeadDetails = ({ lead, includeDivider }: Props) => {
  const { paths } = useContext(RMWContext);
  const { listingAlias } = useSelector((state) => state.properties.currentProperty);
  const {
    loadingByComponent: { InterestedRenters: isInterestedRentersLoading },
  } = useContext(LoadingMapContext);
  const {
    conversationId,
    latestContact: { isPhoneLead, isRead, messageDateMs, messageText, notRepliedTo },
    renterInfo: { renterPhoneNumber, renterName },
    status: { isConversationBlockedByZillow },
    statusLabel: { linkId = '', text = '' } = {},
  } = lead;

  const renterNameDisplay =
    isPhoneLead && !renterName ? renterPhoneNumber || '(Phone lead)' : renterName || 'Name unavailable';
  const shouldShowNewTag = notRepliedTo && linkId !== 'statusResponseOverdue';
  const showUnread = !isRead && !isPhoneLead && !isInterestedRentersLoading;
  const isTourRequestedLead = linkId === 'statusLabelTourRequested';

  return (
    <div data-testid="LeadDetails">
      <Flex data-testid="NameDateContainer" display="flex" justifyContent="space-between" marginBottom="xs">
        <Flex display="flex" flex="1">
          <LoadingText
            data-testid="Name"
            textStyle="body-bold"
            title={renterNameDisplay}
            isLoading={isInterestedRentersLoading}
            loadingWidth="100%"
            css={{ marginRight: '200', ...singleTextLineStyles }}
            className="fs-mask"
          >
            {renterNameDisplay}
          </LoadingText>
        </Flex>

        <LoadingText data-testid="Date" textStyle="body-xs" isLoading={isInterestedRentersLoading} loadingWidth="20%">
          {getMessageDate(messageDateMs)}
        </LoadingText>
      </Flex>

      <Flex data-testid="TagContainer" display="flex" marginBottom="xs">
        {Boolean(shouldShowNewTag || isInterestedRentersLoading) && (
          <LoadingTag
            tone="aqua"
            size="sm"
            css={{ marginRight: '100' }}
            loadingWidth="20%"
            isLoading={isInterestedRentersLoading}
          >
            NEW
          </LoadingTag>
        )}
        {Boolean(linkId || isInterestedRentersLoading) && (
          <LoadingTag
            tone={
              isInterestedRentersLoading
                ? 'gray'
                : getTagTypeByKey(STATUS_KEY.INTERESTED_RENTERS_LEAD_MANAGEMENT, linkId)
            }
            size="sm"
            css={{ marginRight: '100' }}
            loadingWidth="40%"
            isLoading={isInterestedRentersLoading}
          >
            {text}
          </LoadingTag>
        )}
      </Flex>

      <Flex
        data-testid="MessageContainer"
        display="flex"
        alignItems="flex-start"
        flexDirection={{ default: isInterestedRentersLoading ? 'row' : 'column', md: 'row' }}
        gap={{ default: 'xs', md: 'sm' }}
      >
        <Flex display="flex" flex="1">
          {showUnread && (
            <Spacer marginRight="xs">
              <svg xmlns="http://www.w3.org/2000/svg" width="8" height="8" viewBox="0 0 8 8" fill="none">
                <circle cx="4" cy="4" r="4" fill="#006AFF" />
              </svg>
            </Spacer>
          )}
          {isConversationBlockedByZillow ? (
            <Text textStyle="body-sm" asChild>
              <p>
                Zillow has blocked this conversation because it was determined to be a violation of our Good Neighbor
                Policy.{' '}
                <Anchor href="https://www.zillow.com/z/info/good-neighbor-policy/" target="_blank">
                  View policy
                </Anchor>
              </p>
            </Text>
          ) : (
            <>
              {isPhoneLead ? (
                <LoadingText isLoading={isInterestedRentersLoading} loadingWidth="100%">
                  <audio controls={true}>
                    <source src={messageText} />
                  </audio>
                </LoadingText>
              ) : (
                <StyledLineClamp>
                  <LoadingText
                    textStyle="body-sm"
                    isLoading={isInterestedRentersLoading}
                    loadingWidth="100%"
                    className="fs-mask"
                  >
                    {messageText}
                  </LoadingText>
                </StyledLineClamp>
              )}
            </>
          )}
        </Flex>

        {isTourRequestedLead && (
          <StyledRespondNowButtonContainer>
            <TextButton data-testid="RespondNowTourBtn" asChild textStyle="body-sm-bold">
              <a href={`${paths.base}/lead-management/${listingAlias}?conversationId=${conversationId}`}>Respond now</a>
            </TextButton>
          </StyledRespondNowButtonContainer>
        )}
      </Flex>

      {includeDivider && <Divider css={{ marginBlock: '600' }} />}
    </div>
  );
};

export default LeadDetails;
